import { LitElement, html } from 'lit';
import styles from './studio-badge.css.mjs';
import '../studio-icon/studio-icon.mjs';

export class StudioBadge extends LitElement {
  static styles = styles;

  static properties = {
    size: { type: String, reflect: true },
    state: { type: String, reflect: true },
    styleType: { type: String, reflect: true, attribute: 'style-type' },
    content: { type: String, reflect: true },
    iconLeft: { type: String, reflect: true, attribute: 'icon-left' },
    iconRight: { type: String, reflect: true, attribute: 'icon-right' },
    text: { type: String, reflect: true },
    disabled: { type: Boolean, reflect: true },
    color: { type: String, reflect: true },
    background: { type: String, reflect: true },
    borderColor: { type: String, reflect: true },
  };

  static validSizes = ['medium', 'small', 'tiny'];
  static validStates = ['default', 'success', 'info', 'warning', 'error', 'disabled'];
  static validStyleTypes = ['outline', 'filled'];
  static validContents = ['chip', 'badge'];

  constructor() {
    super();
    this.size = 'medium';
    this.state = 'default';
    this.styleType = 'outline';
    this.content = 'chip';
    this.iconLeft = '';
    this.iconRight = '';
    this.text = '';
    this.disabled = false;
    this._eventListeners = [];
    this.color = '';
    this.background = '';
    this.borderColor = '';
  }

  set size(value) {
    this._size = StudioBadge.validSizes.includes(value) ? value : 'medium';
  }

  get size() {
    return this._size;
  }

  set state(value) {
    this._state = StudioBadge.validStates.includes(value) ? value : 'default';
  }

  get state() {
    return this._state;
  }

  set styleType(value) {
    this._styleType = StudioBadge.validStyleTypes.includes(value) ? value : 'outline';
  }

  get styleType() {
    return this._styleType;
  }

  set content(value) {
    this._content = StudioBadge.validContents.includes(value) ? value : 'chip';
  }

  get content() {
    return this._content;
  }

  set iconLeft(value) {
    this._iconLeft = typeof value === 'string' ? value : '';
  }

  get iconLeft() {
    return this._iconLeft;
  }

  set iconRight(value) {
    this._iconRight = typeof value === 'string' ? value : '';
  }

  get iconRight() {
    return this._iconRight;
  }

  set text(value) {
    this._text = typeof value === 'string' ? value : '';
  }

  get text() {
    return this._text;
  }

  get truncatedText() {
    return this._text.length > 16 ? this._text.slice(0, 16) + '...' : this._text;
  }

  firstUpdated() {
    const badge = this.renderRoot.querySelector('.badge');
    if (!badge) return;

    // Prepare for potential interactivity (e.g., clicks)
    this._eventListeners = [
      {
        element: badge,
        event: 'keydown',
        handler: (e) => {
          if (!this.disabled && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            this.dispatchEvent(new Event('click', { bubbles: true, composed: true }));
          }
        },
      },
    ];

    this._eventListeners.forEach(({ element, event, handler }) =>
      element.addEventListener(event, handler)
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._eventListeners.forEach(({ element, event, handler }) =>
      element.removeEventListener(event, handler)
    );
    this._eventListeners = [];
  }

  render() {
    const isBadge = this.content === 'badge';

    // Inline styles for custom colors
    const styleMap = {
      '--custom-text-color': this.color || '',
      '--custom-bg-color': this.background || '',
      '--custom-border-color': this.borderColor ? `${this.borderColor}` : '', // Adjusted to just color value
    };

    const inlineStyle = Object.entries(styleMap)
      .filter(([_, v]) => v) // Remove empty values
      .map(([k, v]) => `${k}: ${v}`)
      .join(';');

    return html`
      <div
        class="badge"
        role="${isBadge ? 'status' : 'img'}"
        aria-label="${isBadge ? this.truncatedText || 'Badge' : 'Icon badge'}"
        aria-disabled="${this.disabled}"
        tabindex="${this.disabled ? -1 : 0}"
        style="${inlineStyle}"
      >
        <slot name="icon-left">
          ${this.iconLeft
            ? html`<studio-icon class="icon left" name="${this.iconLeft}" size="1rem"></studio-icon>`
            : ''}
        </slot>
        <slot name="text">
          ${isBadge && this.text
            ? html`<span class="text">${this.truncatedText}</span>`
            : ''}
        </slot>
        <slot name="icon-right">
          ${isBadge && this.iconRight
            ? html`<studio-icon class="icon right" name="${this.iconRight}" size="1rem"></studio-icon>`
            : ''}
        </slot>
        <slot></slot>
      </div>
    `;
  }
}

customElements.define('studio-badge', StudioBadge);